import { CommunityId, daysAgo } from "@sp-crm/core";
import { Icon } from "components/icon";
import { defaultLinkStyle } from "components/ui/link";
import { RatingDisplay } from "components/ui/rating";
import { Spinner } from "components/ui/spinner";
import {
    CommunityInsightsResult,
    useFindOrCreateCommunityInsightsMutation,
} from "generated/graphql";
import React, { useEffect, useState } from "react";

interface CommunityInsightsReviewsProps {
    communityId: CommunityId;
    showHeader: boolean;
}

export const CommunityInsightsReviews = (props: CommunityInsightsReviewsProps) => {
    const { communityId, showHeader } = props;
    const findOrCreateCommunityInsights = useFindOrCreateCommunityInsightsMutation();

    const [insights, setInsights] = useState<CommunityInsightsResult | null>(null);

    useEffect(() => {
        findOrCreateCommunityInsights.mutate(
            { communityId },
            {
                onSuccess: data => {
                    setInsights(data.findOrCreateCommunityInsights);
                },
            },
        );
    }, [communityId]); // eslint-disable-line react-hooks/exhaustive-deps

    if (!insights) {
        return <Spinner />;
    }

    return (
        <div>
            <div className="flex items-center justify-between mb-4">
                {showHeader ? (
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                            <div className="w-7">
                                <Icon name="22_ClientInterest" />
                            </div>
                            <span className="text-lg text-gray-700">Google Reviews</span>
                        </div>
                    </div>
                ) : null}
                <div className="flex items-center space-x-2">
                    {insights.averageRating ? (
                        <p className="text-gray-600">
                            {insights.averageRating.toFixed(1)}
                        </p>
                    ) : null}
                    <RatingDisplay rating={insights.averageRating} />
                    {insights.ratingCount ? (
                        <p className="text-gray-600">({insights.ratingCount})</p>
                    ) : null}
                </div>
            </div>
            <div>
                {insights.reviews.length < 1 ? (
                    <p className="text-gray-500">
                        No reviews exist for this community yet.
                    </p>
                ) : (
                    <div>
                        <p className="text-gray-500 mb-2 text-sm">
                            Google&apos;s most relevant reviews are displayed below. View
                            all reviews for this community at its{" "}
                            <a
                                className={defaultLinkStyle}
                                href={insights.googleMapsUrl}
                                target="_blank"
                                rel="noreferrer">
                                Google Maps listing
                            </a>
                            .
                        </p>
                        {insights.reviews.map(review => (
                            <div
                                key={review.id}
                                className="border-t border-gray-200 pt-6 pb-2">
                                <div className="flex items-start">
                                    <div className="flex-shrink-0">
                                        <img
                                            className="h-10 w-10 rounded-full"
                                            src={review.authorPhotoUri}
                                            alt={review.authorDisplayName}
                                        />
                                    </div>
                                    <div className="ml-4">
                                        <div className="flex items-center">
                                            <h4 className="text-sm font-medium text-gray-900">
                                                {review.authorDisplayName}
                                            </h4>
                                            <span className="ml-2 text-sm text-gray-500">
                                                {daysAgo(review.publishedAt)}
                                            </span>
                                        </div>
                                        <div className="mt-1 flex items-center">
                                            <RatingDisplay rating={review.rating} />
                                        </div>
                                        <p className="mt-2 text-sm text-gray-700">
                                            {review.text}
                                        </p>
                                        <div className="mt-2">
                                            <a
                                                href={review.webUri}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="text-sm text-brand-600 hover:text-brand-800">
                                                View on Google
                                            </a>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
            <div className="mt-4">
                <p className="text-gray-500 text-sm">
                    Last updated {daysAgo(insights.lastGooglePlaceSynchronizedAt)}.
                </p>
            </div>
        </div>
    );
};
